import firebase from "firebase/compat/app";
import has from 'lodash/has';
import pick from 'lodash/pick';
import get from 'lodash/get';
import padStart from "lodash/padStart";
import {
  IonItem, IonLabel,
  IonToggle, IonTextarea,
} from "@ionic/react";
import React from "react";
import {string2Number} from "./utils";
import {DateTime} from "luxon";
import {InputProps} from "../components/forms/Input";


export const re_telefone = /^\(\d{2}\) \d{4}\d?-\d{4}$/;
export const re_cep = /^\d{5}-\d{3}$/;
export const re_email = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

interface ValidationResponse {
  success: boolean;
  error?: string;
}

export interface FormField {
  name: string;
  label: string;
  required?: boolean;
  type: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'telefone' | 'email' | 'cep' |
        'checkbox' | 'text' | 'select' | 'select-multiple' | 'currency' | 'image' | 'searchInput' |
        'cnpj' | 'raw-upload';
  validation?: (value:any) => ValidationResponse;
}

export interface FormDefinition {
  [field:string]: FormField
}

interface ErrorMap {
  [field: string]: string[]
}

export interface ValidationResult {
  success: boolean;
  fieldsOk: string[];
  fieldsWithError: string[];
  messagesFlat: string[];
  messages: ErrorMap;
  values: any;
}

export interface FieldState {
  value: any;
  hasError: boolean;
  errorMessage: string;
  touched: boolean;
}

export interface FormState {
  [field:string]: FieldState;
}

export const forms = {
  validateField: (field:FormField, state:FieldState, value?:any, touched:boolean=true): FieldState => {
    let hasError:boolean = false;
    let errorMessage:string = '';
    let fieldValue;
    const required = get(field, 'required', false);

    // obter valor do campo
    if (value !== undefined) {
      if (field.type === 'checkbox') {
        if (Array.isArray(value)) {
          // setando manualmente o valor
          fieldValue = value;
        } else if (typeof value === 'object') {
          // valor via controle de form
          const cValue = value.value;
          if (value.checked) {
            if (state.value.indexOf(value.value) === -1)
              fieldValue = [...state.value, cValue];
            else
              fieldValue = state.value;
          } else {
            fieldValue = state.value.filter((i: string) => i !== cValue);
          }
        }
      } else if (field.type === 'date') {
        if (value === null) {
          fieldValue = undefined;
        }
        else if (typeof value === 'object') {
          fieldValue = value.value;
        } else {
          fieldValue = value;
        }
      } else {
        fieldValue = value;
      }
    } else {
      fieldValue = state.value;
    }

    // check required
    if (field.type === 'checkbox' || field.type === 'select-multiple') {
      if (touched && (!fieldValue || fieldValue.length === 0) && required) {
        errorMessage = `O campo "${field.label}" deve ser preenchido.`;
        hasError = true;
      }
    } else {
      if (touched && !fieldValue && required) {
        errorMessage = `O campo "${field.label}" deve ser preenchido.`;
        hasError = true;
      }
    }

    // check formats
    if (fieldValue) {
      if (field.type === 'telefone') {
        // validar telefone completo
        if (!re_telefone.exec(fieldValue)) {
          errorMessage = `O campo "${field.label}" não é válido.`;
          hasError = true;
        }
      }
      else if (field.type === 'cep') {
        // validar se cep completo
        if (!re_cep.exec(fieldValue)) {
          errorMessage = `O campo "${field.label}" não é válido.`;
          hasError = true;
        }
      }
      else if (field.type === 'email') {
        // validar email
        if (!re_email.test(fieldValue)) {
          errorMessage = `O campo "${field.label}" não é válido.`;
          hasError = true;
        }
      }
    }

    return {
      ...state,
      hasError,
      errorMessage,
      touched,
      value: fieldValue,
    };
  },

  buildFormState: (formDef:FormDefinition, initialValues?:any): FormState => {
    const state: FormState = {};

    Object.keys(formDef).forEach(name => {
      state[name] = forms.buildFieldState(formDef[name], get(initialValues, name));
    });

    return state;
  },

  buildFieldState: (field:FormField, initialValue?:any): FieldState => {
    let value;
    if (initialValue !== undefined) {
      // formatação inicial dos campos
      if (field.type === 'date' && initialValue) {
        try {
          if (typeof initialValue === 'string')
            value = initialValue;
          else
            value = DateTime.fromJSDate(initialValue.toDate()).toLocal().toISO();
        } catch (error) {
          console.log(`Invalid date "${initialValue}"`);
        }
      } else if (field.type === 'currency') {
        value = forms.utils.format.currency(initialValue);
      } else if (field.type === 'select' || field.type === 'searchInput') {
        if (typeof initialValue === 'object')
          value = initialValue.id;
        else
          value = initialValue;
      } else if (field.type === 'select-multiple') {
        if (!initialValue)
          value = [];
        else if (Array.isArray(initialValue))
          value = initialValue.map(i => has(i, 'id') ? i.id : i);
        else if (typeof initialValue === 'object')
          value = [initialValue.id];
        else
          value = [initialValue];
      } else if (field.type === 'checkbox') {
        // aceitar migracao de lista/select p/ checkbox
        if (typeof initialValue === 'string')
          value = [initialValue];
        else
          value = initialValue
      } else {
        value = initialValue;
      }
    } else {
      // Inicialização dos campos vazios
      if (field.type === 'string')
        value = '';
      else if (field.type === 'boolean')
        value = false;
      else if (field.type === 'checkbox')
        value = [];
      else if (field.type === 'select-multiple')
        value = [];
      else
        value = '';
    }

    return {
      value,
      hasError: false,
      errorMessage: '',
      touched: false,
    };
  },

  validate: (formDef:FormDefinition, formState:FormState): {result: ValidationResult, state: FormState} => {
    const fieldsOk: string[] = [];
    const fieldsWithError: string[] = [];
    const messagesFlat: string[] = [];
    const messages: ErrorMap = {};
    const values: any = {};
    let success:boolean = true;

    const appendError = (f:string, e:string): void => {
      fieldsWithError.push(f);
      messagesFlat.push(e);
      if (!has(messages, f))
        messages[f] = [];

      messages[f].push(e);
      success = false;
    };

    const newFormState:FormState = {};
    Object.keys(formDef).forEach(name => {
      const state = forms.validateField(formDef[name], formState[name]);

      if (state.hasError) {
        appendError(name, state.errorMessage);
      } else {
        fieldsOk.push(name);
        values[name] = forms.normalize(formDef[name], state.value);
      }

      // update field state
      newFormState[name] = state;
    });

    return {
      result: {values, success, fieldsOk, fieldsWithError, messagesFlat, messages},
      state: newFormState,
    };
  },

  normalize: (field: FormField, value:any): any => {
    switch (field.type) {
      case "number":
      case "currency":
        if (typeof value === "string" && !!value)
          return string2Number(value);
        else
          return value;
      case "date":
        if (value)
          return firebase.firestore.Timestamp.fromDate(new Date(value));
        else
          return value;
      default:
        return value;
    }
  },

  utils: {
    format: {
      cnpj: (value: string): string => {
        let cnpj = value.replace(/\D/g, "");

        // ajuste zeros a esquerda
        if (cnpj.length === 13)
          cnpj = padStart(cnpj, 14, '0');
        else if (cnpj.length === 15 && cnpj.startsWith('0'))
          cnpj = cnpj.replace(/^0/, '');

        // cortar digitos excedentes
        if (cnpj.length > 14)
          cnpj = cnpj.slice(0, 14);

        if (cnpj.length >= 3 && cnpj.length <= 6)
          cnpj = cnpj.replace(/(\d+)(\d{2})$/, "$1-$2");
        else if (cnpj.length >= 7 && cnpj.length <= 9)
          cnpj = cnpj.replace(/(\d+)(\d{4})(\d{2})$/, "$1/$2-$3");
        else if (cnpj.length >= 10 && cnpj.length <= 12)
          cnpj = cnpj.replace(/(\d+)(\d{3})(\d{4})(\d{2})$/, "$1.$2/$3-$4");
        else if (cnpj.length === 14)
          cnpj = cnpj.replace(
            /(\d+)(\d{3})(\d{3})(\d{4})(\d{2})$/,
            "$1.$2.$3/$4-$5"
          );
        return cnpj;
      },

      telefone: (value: string): string => {
        let telefone = value.replace(/\D/g, '');
        const digitos = telefone.split('');

        if (digitos.length === 10) {
          telefone = `(${digitos.slice(0, 2).join('')}) ${digitos.slice(2, 6).join('')}-${digitos.slice(6).join('')}`;
        } else if (digitos.length === 11) {
          telefone = `(${digitos.slice(0, 2).join('')}) ${digitos.slice(2, 7).join('')}-${digitos.slice(7).join('')}`;
        } else if (digitos.length > 2) {
          telefone = `(${digitos.slice(0, 2).join('')}) ${digitos.slice(2).join('')}`;
        }

        return telefone;
      },

      cep: (value: string): string => {
        let cep = value.replace(/\D/g, '');
        const digitos = cep.split('');

        if (digitos.length >= 8)
          cep = `${digitos.slice(0, 5).join('')}-${digitos.slice(5).join('')}`;

        return cep;
      },

      currency: (value:string|number|undefined|null):string => {
        if (!value)
          return '';

        if (typeof value === 'number')
          value = value.toString();

        let res:string = value.replace(/\D/g, '');
        if (!res)
          return '';
        res = Number.parseInt(res).toString();
        if (res === '0')
          return '';

        const digitos = res.toString().split('');
        if (digitos.length === 1) {
          return `0,0${digitos[0]}`;
        } if (digitos.length === 2) {
          return `0,${digitos.join('')}`;
        } else if (digitos.length <= 5) {
          return res.replace(/(\d+)(\d{2})$/, '$1,$2');
        } else {
          const match = res.match(/(\d+)(\d{2})$/);
          if (match) {
            return match[1].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.') + ',' + match[2];
          } else {
            return res;
          }
        }
      }
    }
  },
};

interface ToggleProps extends InputProps {

}

export const Toggle = (props:ToggleProps) => {
  const onChange = (e:any) => {
    props.onChange(props.definition.name, e.detail.checked);
  };

  return (
    <IonItem
      disabled={props.disabled}
      lines="full"
      className="app-toggle"
    >
      <IonLabel>
        <span className={(props.state.touched && props.state.hasError) ? "text-red-600" : ""}>
          {props.definition.label} {props.definition.required && '*'}
        </span>
      </IonLabel>
      <IonToggle
        value={props.definition.name}
        checked={props.state.value}
        onIonChange={onChange}
      />
    </IonItem>
  );
};

interface TextareaProps extends InputProps {
  rows?: number,
}

export const Textarea = (props:TextareaProps) => {
  const onChange = (e:any) => {
    props.onChange(props.definition.name, e.detail.value);
  };

  const iprops:any = {};
  Object.assign(iprops, pick(props, ['rows']));

  return (
    <IonItem disabled={props.disabled}>
      <IonLabel position="floating">
        <span className={(props.state.touched && props.state.hasError) ? "text-red-600" : ""}>
          {props.definition.label} {props.definition.required && '*'}
        </span>
      </IonLabel>
      <IonTextarea
        {...iprops}
        value={props.state.value}
        onIonChange={onChange}
      />
    </IonItem>
  );
};
